import { type KeyboardEvent, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { LimitNote, NotSavedNote, overLimit } from "@/components/ui/Note";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { Textarea } from "@/components/ui/textarea";

/**
 * A title or a line edited where it is drawn. Enter saves, Escape puts it back, and an
 * emptied line puts it back too: a section cannot lose its title by being cleared. Leaving
 * the field saves what was typed, so an edit is never lost to a click elsewhere.
 *
 * Enter and Escape both leave the field, and leaving is the one place an edit is sent, so a
 * line is never sent twice under one version.
 *
 * A field with a `maxLength` counts the characters as the words near it, and words past it
 * stay in the field unsent, with how much to remove. Words the server refuses stay too, said
 * to be unsaved, so the attorney's typing is never lost to a refusal.
 */
export function InlineLine({
  value,
  label,
  readOnly,
  className,
  wrap = false,
  maxLength,
  onSave,
}: {
  value: string;
  /** The field's name for a reader: it has no visible label. */
  label: string;
  readOnly: boolean;
  className?: string;
  /** Lets long words wrap onto more lines; Enter still saves, so the words stay one line of text. */
  wrap?: boolean;
  /** The longest words the server takes. */
  maxLength?: number;
  /** Saves the words; `refused` is called if the server does not take them. */
  onSave: (next: string, refused: () => void) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [shown, setShown] = useState(value);
  const [unsaved, setUnsaved] = useState(false);
  useUnsavedDraft(unsaved);
  const cancelledRef = useRef(false);
  const noteId = useId();
  if (shown !== value) {
    // The server answered with other words (another edit landed): show those.
    setShown(value);
    setDraft(value);
    setUnsaved(false);
  }
  if (readOnly) return <span className={className}>{value}</span>;
  const next = draft.trim();
  const tooLong = overLimit(next, maxLength);
  const send = () => {
    setUnsaved(false);
    onSave(next, () => {
      setUnsaved(true);
    });
  };
  const editing = {
    "aria-label": label,
    "aria-invalid": tooLong || unsaved || undefined,
    "aria-describedby": tooLong || unsaved ? noteId : undefined,
    value: draft,
    className,
    onChange: (event: { target: { value: string } }) => {
      setDraft(event.target.value);
      setUnsaved(false);
    },
    onBlur: () => {
      if (cancelledRef.current || next === "" || next === value) {
        cancelledRef.current = false;
        setDraft(value);
        setUnsaved(false);
        return;
      }
      if (tooLong) return;
      send();
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      lineKey(event, tooLong, cancelledRef);
    },
  };
  const field = wrap ? (
    <Textarea appearance="line" rows={1} {...editing} />
  ) : (
    <Input appearance="inline" {...editing} />
  );
  if (maxLength === undefined && !unsaved) return field;
  return (
    <span className="flex min-w-0 flex-1 flex-col">
      {field}
      <LineNote
        id={noteId}
        unsaved={unsaved}
        onRetry={send}
        length={next.length}
        maxLength={maxLength}
      />
    </span>
  );
}

/** Enter leaves the line to save it, unless the words are too long; Escape leaves it unsaved. */
function lineKey(
  event: KeyboardEvent<HTMLElement>,
  tooLong: boolean,
  cancelledRef: { current: boolean },
) {
  if (event.key === "Escape" || (event.key === "Enter" && !tooLong)) {
    event.preventDefault();
    cancelledRef.current = event.key === "Escape";
    event.currentTarget.blur();
  } else if (event.key === "Enter") {
    event.preventDefault();
  }
}

/** Under the line: that the server refused the words, else how near they are to the limit. */
function LineNote({
  id,
  unsaved,
  onRetry,
  length,
  maxLength,
}: {
  id: string;
  unsaved: boolean;
  onRetry: () => void;
  length: number;
  maxLength?: number;
}) {
  if (unsaved) return <NotSavedNote id={id} failed onRetry={onRetry} />;
  return maxLength === undefined ? null : (
    <LimitNote id={id} length={length} max={maxLength} />
  );
}
